import { rewriteUrl } from '@fullstory/url-rewriter';
import {
  Atrule,
  AtrulePrelude,
  AttributeSelector,
  Block,
  Combinator,
  CssLocation,
  CssNode,
  Declaration,
  Dimension,
  FunctionNode,
  Identifier,
  List,
  MediaFeature,
  MediaQueryList,
  PseudoClassSelector,
  Raw,
  SelectorList,
  StringNode,
  TypeSelector,
  Value,
} from '@sethfowler/css-tree';

export const mutatedTag = '__fs__mutated__';

// A special property that we use to mark AST nodes as mutated. An AST node is
// considered mutated if:
//  - Its properties were changed.
//  - It's a new node that was added to the AST.
//  - It previously contained a child node that was removed without being
//    replaced. (If the child node was replaced, the replacement nodes are
//    enough to indicate that a mutation occurred. Marking the parent of
//    replaced nodes as mutated is correct but unnecessary.)
//
// interpolateMutations() uses this information to determine when it needs to
// generate CSS source for an AST subtree. For nodes that haven't been mutated,
// we can simply copy over the original source.
type PossiblyTaggedAsMutated = { [mutatedTag]?: boolean };
type TaggedAsMutated = { [mutatedTag]: true };

type PossiblyMutatedSubtree<T extends CssNode> = {
  [P in keyof T]: T[P] extends CssNode
  ? (PossiblyMutatedSubtree<T[P]> & PossiblyTaggedAsMutated)
  : T[P];
}
export type PossiblyMutated<T extends CssNode> =
  PossiblyMutatedSubtree<T> & PossiblyTaggedAsMutated;

// TODO(sethf): This type is almost the same as PossiblyMutatedSubtree, and it
// would be nice to simply use one recursive type that would accept a second
// type parameter, but that seems to trigger an infinite loop in the TypeScript
// compiler for reasons that aren't clear to me. (Perhaps it prevents it from
// instantiating the recursion lazily?) We can try again after updating to
// TypeScript 3.7, which apparently has better support for recursive types.
type MutatedSubtree<T extends CssNode> = {
  [P in keyof T]: T[P] extends CssNode
  ? (MutatedSubtree<T[P]> & TaggedAsMutated)
  : T[P];
}
export type Mutated<T extends CssNode> =
  MutatedSubtree<T> & TaggedAsMutated;

// Create an attribute selector. Providing only 'name' will create a '[name]'
// selector. Provide a matcher (e.g. '=') and a value (e.g. '"foo"') to create
// an '[name="foo"]'-style selector. Note that if you want quotes to appear in
// the output, you need to include them in 'value'.
export function createAttributeSelector(
  name: string,
  matcher: AttributeSelector['matcher'] = null,
  value: AttributeSelector['value'] = null,
): Mutated<AttributeSelector> {
  return {
    type: 'AttributeSelector',
    name: {
      type: 'Identifier',
      name,
      [mutatedTag]: true,
    },
    matcher,
    value,
    flags: null,
    [mutatedTag]: true,
  };
}

// Create a type selector. (i.e., a tag selector)
export function createTypeSelector(name: string): Mutated<TypeSelector> {
  return {
    type: 'TypeSelector',
    name,
    [mutatedTag]: true,
  };
}

export function createDeclaration(
  property: string,
  val: Mutated<Value | Raw>,
  important: boolean | string = false,
): Mutated<Declaration> {
  return {
    type: 'Declaration',
    important,
    property,
    value: val,
    [mutatedTag]: true,
  };
}

export function createValue(...nodes: CssNode[]): Mutated<Value> {
  const children = new List<CssNode>().fromArray(nodes);
  return {
    type: 'Value',
    children,
    [mutatedTag]: true,
  };
}

export function createRaw(value: string): Mutated<Raw> {
  return {
    type: 'Raw',
    value,
    [mutatedTag]: true,
  };
}

// Create a StringNode.
export function createString(value: string): Mutated<StringNode> {
  return {
    type: 'String',
    value,
    [mutatedTag]: true,
  };
}

export function createIdentifier(name: string): Mutated<Identifier> {
  return {
    type: 'Identifier',
    name,
    [mutatedTag]: true,
  };
}

export function createFunction(name: string, ...body: CssNode[]): Mutated<FunctionNode> {
  const children = new List<CssNode>().fromArray(body);
  return {
    type: 'Function',
    name,
    children,
    [mutatedTag]: true,
  };
}

export function createMediaAtrule(prelude: Mutated<AtrulePrelude> | null, loc: CssLocation | undefined, block: PossiblyMutated<Block> | null): Mutated<Atrule> {
  return {
    loc,
    type: 'Atrule',
    [mutatedTag]: true,
    prelude,
    name: 'media',
    block,
  };
}

export function createMediaQueryList(loc: CssLocation | undefined, ...children: CssNode[]): Mutated<MediaQueryList> {
  return {
    type: 'MediaQueryList',
    loc,
    children: new List<CssNode>().fromArray(children),
    [mutatedTag]: true,
  };
}

export function createAtrulePrelude(loc: CssLocation | undefined, ...children: CssNode[]): Mutated<AtrulePrelude> {
  return {
    type: 'AtrulePrelude',
    loc,
    children: new List<CssNode>().fromArray(children),
    [mutatedTag]: true,
  };
}
export function createDimension(loc: CssLocation | undefined, value: string, unit: string = ''): Mutated<Dimension> {
  return {
    type: 'Dimension',
    loc,
    value,
    unit,
    [mutatedTag]: true,
  };
}

export function createBlock(loc: CssLocation | undefined, ...children: CssNode[]): Mutated<Block> {
  return {
    type: 'Block',
    loc,
    children: new List<CssNode>().fromArray(children),
    [mutatedTag]: true,
  };
}

export function createAttributeEqualsSelector(loc: CssLocation | undefined, name: string, value: string): Mutated<AttributeSelector> {
  return {
    type: 'AttributeSelector',
    name: {
      type: 'Identifier',
      name,
      [mutatedTag]: true,
    },
    matcher: '=',
    value: {
      type: 'String',
      value,
      [mutatedTag]: true,
    } as Mutated<StringNode>,
    flags: null,
    [mutatedTag]: true,
    loc,
  };
}

export type AttributesMediaFeature = '_fs_pointer' | '_fs_prefers_color_scheme';
export type MediaRewriteSelector = Mutated<AttributeSelector> | Mutated<PseudoClassSelector>;

export function createSelectorFromMediaFeature(node: MediaFeature, fsAttribute: AttributesMediaFeature): MediaRewriteSelector {
  const identifier = node.value as Identifier;
  // loc is intentional undefined here because it needs to be set by code that adds them to rules
  return createAttributeEqualsSelector(undefined, fsAttribute, `"${identifier.name}"`);
}

export function createNegation(node: CssNode): Mutated<PseudoClassSelector> {
  if (node.type === 'SelectorList') {
    return createPseudoClassSelector('not', node.loc, node);
  } else {
    return createPseudoClassSelector('not', node.loc, createSelectorList(node.loc, node));
  }
}

export function createPseudoClassSelector(name: string, loc: CssLocation | undefined, ...children: CssNode[]): Mutated<PseudoClassSelector> {
  return {
    type: 'PseudoClassSelector',
    loc,
    name,
    // note: css tree will serialize differently based on children if it's an empty array or null
    // empty array => :pseudo()
    // null => :pseudo
    // for our case we only use it for root and not, so when no arguments is passed for children, we want null
    children: children.length === 0 ? null : new List<CssNode>().fromArray(children),
    [mutatedTag]: true,
  };
}

export function createSelectorList(loc: CssLocation | undefined, ...selectors: CssNode[]): Mutated<SelectorList> {
  return {
    type: 'SelectorList',
    loc,
    children: new List<CssNode>().fromArray([
      {
        type: 'Selector',
        loc,
        children: new List<CssNode>().fromArray(selectors),
      },
    ]),
    [mutatedTag]: true,
  };
}

export function createCombinator(name: string): Combinator {
  return {
    type: 'Combinator',
    name,
  };
}

export function testRewriteUrl(url: string): string {
  return rewriteUrl('http://base.url', url);
}
